"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { PushNotifications, type Token } from "@capacitor/push-notifications";
import { createClient } from "@/lib/supabase/client";
import { safeGetUser } from "@/lib/supabase/safe-get-user";
import {
  encodeSubscriptionKeys,
  ensurePushSubscription,
  getCurrentSubscription,
  getReadyServiceWorker,
  withTimeout,
} from "@/lib/push-helpers";
import {
  subscribeUser,
  unsubscribeUser,
} from "@/app/actions/notification-settings";

// VAPID public key - should be set in environment variables
const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY || "";

// Native (Capacitor) push registration timeouts — mirror the partner app so a
// stalled bridge / missing APNs token never leaves the UI spinning forever.
const NATIVE_PERMISSION_CHECK_MS = 10_000;
const NATIVE_REGISTER_TIMEOUT_MS = 20_000;

type PermissionStatus = "granted" | "denied" | "default" | "prompt";

interface PushNotificationState {
  isSupported: boolean;
  isSubscribed: boolean;
  permission: PermissionStatus;
  isLoading: boolean;
  error: string | null;
  token: string | null; // 네이티브용 FCM/APNs 토큰
  autoSubscribeAttempted: boolean;
  dbPushEnabled: boolean | null;
}

export function usePushNotifications() {
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    isSubscribed: false,
    permission: "default",
    isLoading: true,
    error: null,
    token: null,
    autoSubscribeAttempted: false,
    dbPushEnabled: null,
  });

  const supabase = createClient();
  const isNative = Capacitor.isNativePlatform();

  // Track the native push listeners THIS hook attaches so we can remove just
  // them — never PushNotifications.removeAllListeners(), which would also kill
  // the global pushNotificationActionPerformed listener in CapacitorBootstrap.
  const nativeHandlesRef = useRef<PluginListenerHandle[]>([]);

  const clearNativeHandles = useCallback(async () => {
    const handles = nativeHandlesRef.current;
    nativeHandlesRef.current = [];
    await Promise.all(handles.map((h) => h.remove().catch(() => {})));
  }, []);

  // =====================
  // 네이티브 푸시 알림 (FCM/APNs)
  // =====================

  const checkNativeSupport = useCallback(async () => {
    try {
      const result = await PushNotifications.checkPermissions();
      return result.receive !== "denied";
    } catch {
      return false;
    }
  }, []);

  const subscribeNative = useCallback(async (): Promise<boolean> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    // 권한 확인. checkPermissions 는 즉답이어야 하므로 타임아웃을 건다.
    // requestPermissions 는 OS 권한 다이얼로그를 기다리므로 타임아웃 없음(정상).
    try {
      let permStatus = await withTimeout(
        PushNotifications.checkPermissions(),
        NATIVE_PERMISSION_CHECK_MS,
        "PushNotifications.checkPermissions"
      );
      if (permStatus.receive === "prompt") {
        permStatus = await PushNotifications.requestPermissions();
      }
      if (permStatus.receive !== "granted") {
        setState((prev) => ({
          ...prev,
          permission: permStatus.receive as PermissionStatus,
          isLoading: false,
          error: "Push notification permission denied",
        }));
        return false;
      }
    } catch (error) {
      console.error("Native push permission check failed:", error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Failed to check push permission",
      }));
      return false;
    }

    // 이전 시도의 리스너 정리(중복 방지) 후, register() 전에 리스너를 먼저 단다
    // — 토큰이 빨리 도착해도 유실되지 않도록(register 후 부착 시 레이스).
    await clearNativeHandles();

    return new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (val: boolean) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(val);
      };

      // APNs/FCM 토큰은 보통 1초 안에 오지만, entitlement 누락/무네트워크 시
      // 영원히 안 올 수 있으므로 상한을 둬 무한 로딩을 막는다.
      const timer = window.setTimeout(() => {
        console.warn("[push] native registration timed out (no token)");
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: "Push registration timed out",
        }));
        finish(false);
      }, NATIVE_REGISTER_TIMEOUT_MS);

      void (async () => {
        try {
          const regHandle = await PushNotifications.addListener(
            "registration",
            async (token: Token) => {
              try {
                const result = await subscribeUser({
                  endpoint: `native:${Capacitor.getPlatform()}:${token.value}`,
                  keys: {
                    p256dh: token.value,
                    auth: Capacitor.getPlatform(),
                  },
                });
                if (!result.success) {
                  console.warn(
                    "[usePushNotifications] subscribeUser failed:",
                    result.error
                  );
                  setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: "Failed to register subscription with server",
                  }));
                  finish(false);
                  return;
                }
                setState((prev) => ({
                  ...prev,
                  isSubscribed: true,
                  permission: "granted",
                  isLoading: false,
                  token: token.value,
                }));
                finish(true);
              } catch (error) {
                console.warn(
                  "[usePushNotifications] subscribeUser threw:",
                  error
                );
                setState((prev) => ({
                  ...prev,
                  isLoading: false,
                  error: "Failed to register subscription with server",
                }));
                finish(false);
              }
            }
          );

          const errHandle = await PushNotifications.addListener(
            "registrationError",
            (error) => {
              console.error("Push registration error:", error);
              setState((prev) => ({
                ...prev,
                isLoading: false,
                error: "Failed to register for push notifications",
              }));
              finish(false);
            }
          );

          const recvHandle = await PushNotifications.addListener(
            "pushNotificationReceived",
            (notification) => {
              console.log("Push received:", notification);
            }
          );

          nativeHandlesRef.current.push(regHandle, errHandle, recvHandle);

          // 리스너 부착 완료 — 이제 토큰을 요청한다.
          await PushNotifications.register();
        } catch (error) {
          console.error("Native push subscription error:", error);
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: "Failed to subscribe to push notifications",
          }));
          finish(false);
        }
      })();
    });
  }, [clearNativeHandles]);

  const unsubscribeNative = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await clearNativeHandles();

      if (state.token) {
        await unsubscribeUser(`native:${Capacitor.getPlatform()}:${state.token}`);
      }

      setState((prev) => ({
        ...prev,
        isSubscribed: false,
        isLoading: false,
        token: null,
      }));

      return true;
    } catch (error) {
      console.error("Native push unsubscription error:", error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Failed to unsubscribe from push notifications",
      }));
      return false;
    }
  }, [state.token, clearNativeHandles]);

  // =====================
  // 웹 푸시 알림 (Web Push API)
  // =====================

  const checkWebSupport = useCallback(() => {
    return (
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  }, []);

  const registerServiceWorker = useCallback(async () => {
    if (!("serviceWorker" in navigator)) return null;

    try {
      const registration = await getReadyServiceWorker();
      console.log("Service Worker registered:", registration.scope);
      return registration;
    } catch (error) {
      console.error("Service Worker registration failed:", error);
      return null;
    }
  }, []);

  const checkWebSubscription = useCallback(async () => {
    if (!checkWebSupport()) {
      setState((prev) => ({
        ...prev,
        isSupported: false,
        isLoading: false,
      }));
      return;
    }

    try {
      await registerServiceWorker();
      const subscription = await getCurrentSubscription();
      if (subscription && Notification.permission === "granted") {
        const result = await subscribeUser({
          endpoint: subscription.endpoint,
          keys: encodeSubscriptionKeys(subscription),
        });
        if (!result.success) {
          console.warn("[push] failed to refresh existing subscription:", result.error);
        }
      }

      setState((prev) => ({
        ...prev,
        isSupported: true,
        isSubscribed: !!subscription,
        permission: Notification.permission as PermissionStatus,
        isLoading: false,
      }));
    } catch (error) {
      console.error("Error checking subscription:", error);
      setState((prev) => ({
        ...prev,
        isSupported: true,
        isLoading: false,
        error: "Failed to check subscription status",
      }));
    }
  }, [checkWebSupport, registerServiceWorker]);

  const subscribeWeb = useCallback(async () => {
    if (!VAPID_PUBLIC_KEY) {
      setState((prev) => ({
        ...prev,
        error: "Push notifications are not configured",
      }));
      return false;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const user = await safeGetUser(supabase, { retries: 1, baseDelayMs: 150 });

      if (!user) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: "Please log in to enable notifications",
        }));
        return false;
      }

      const permission = await Notification.requestPermission();

      if (permission !== "granted") {
        setState((prev) => ({
          ...prev,
          permission: permission as PermissionStatus,
          isLoading: false,
          error: "Notification permission denied",
        }));
        return false;
      }

      const subscription = await ensurePushSubscription(VAPID_PUBLIC_KEY);
      const result = await subscribeUser({
        endpoint: subscription.endpoint,
        keys: encodeSubscriptionKeys(subscription),
      });

      if (!result.success) {
        console.error("Error saving subscription:", result.error);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: result.error || "Failed to save subscription",
        }));
        return false;
      }

      setState((prev) => ({
        ...prev,
        isSubscribed: true,
        permission: "granted",
        isLoading: false,
      }));

      return true;
    } catch (error) {
      console.error("Error subscribing to push:", error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Failed to subscribe to notifications",
      }));
      return false;
    }
  }, [supabase]);

  const unsubscribeWeb = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const subscription = await getCurrentSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await unsubscribeUser(subscription.endpoint);
      }

      setState((prev) => ({
        ...prev,
        isSubscribed: false,
        isLoading: false,
      }));

      return true;
    } catch (error) {
      console.error("Error unsubscribing:", error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Failed to unsubscribe from notifications",
      }));
      return false;
    }
  }, []);

  // =====================
  // 통합 API
  // =====================

  const checkSubscription = useCallback(async () => {
    if (isNative) {
      const supported = await checkNativeSupport();
      const permStatus = await PushNotifications.checkPermissions();
      setState((prev) => ({
        ...prev,
        isSupported: supported,
        permission: permStatus.receive as PermissionStatus,
        isLoading: false,
      }));
    } else {
      await checkWebSubscription();
    }
  }, [isNative, checkNativeSupport, checkWebSubscription]);

  const subscribe = useCallback(async () => {
    if (isNative) {
      return subscribeNative();
    }
    return subscribeWeb();
  }, [isNative, subscribeNative, subscribeWeb]);

  const unsubscribe = useCallback(async () => {
    if (isNative) {
      return unsubscribeNative();
    }
    return unsubscribeWeb();
  }, [isNative, unsubscribeNative, unsubscribeWeb]);

  // Auto-subscribe: if user has push enabled in DB and this device isn't
  // registered yet, auto-register (requesting browser permission if needed).
  // This handles the "logged in on a new device" scenario — the user already
  // opted in on another device, so we skip the marketing consent dialog and
  // go straight to the browser's native permission prompt.
  const autoSubscribeIfNeeded = useCallback(async () => {
    const markDone = (dbPush?: boolean) =>
      setState((prev) => ({
        ...prev,
        autoSubscribeAttempted: true,
        ...(dbPush !== undefined ? { dbPushEnabled: dbPush } : {}),
      }));

    if (isNative || !checkWebSupport()) { markDone(); return; }

    try {
      const existing = await getCurrentSubscription();
      if (existing) { markDone(); return; } // already subscribed on this device

      const user = await safeGetUser(supabase);
      if (!user) { markDone(); return; }

      const { data: profile } = await supabase
        .from("customer_profiles")
        .select("notification_preferences")
        .eq("user_id", user.id)
        .maybeSingle();

      const prefs = profile?.notification_preferences as { push?: boolean } | null;
      const pushEnabled = prefs?.push === true;
      if (!pushEnabled) { markDone(false); return; }

      // DB says push is ON — check browser permission
      if (Notification.permission === "denied") { markDone(true); return; }

      // "granted" (re-subscribe silently) or "default" (browser prompt)
      await subscribeWeb();
      markDone(true);
    } catch {
      // Silent fail — auto-subscribe is best-effort
      markDone();
    }
  }, [isNative, checkWebSupport, supabase, subscribeWeb]);

  // 초기화
  useEffect(() => {
    const initTimer = window.setTimeout(() => {
      void checkSubscription().then(() => {
        void autoSubscribeIfNeeded();
      });
    }, 0);

    return () => {
      window.clearTimeout(initTimer);
      if (isNative) {
        void clearNativeHandles();
      }
    };
  }, [checkSubscription, autoSubscribeIfNeeded, isNative, clearNativeHandles]);

  return {
    ...state,
    subscribe,
    unsubscribe,
    checkSubscription,
    isNative,
  };
}
